import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { collection, query, getDocs, where, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';

export const CommunityDebug: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [testResults, setTestResults] = useState<any>({});
  const [testing, setTesting] = useState(false);

  const addDebug = (message: string) => {
    console.log(`🔍 DEBUG: ${message}`);
    setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  useEffect(() => {
    addDebug('CommunityDebug component mounted');
    addDebug(`Auth loading: ${authLoading}`);
    addDebug(`User exists: ${!!user}`);
    if (user) {
      addDebug(`User ID: ${user.uid}`);
      addDebug(`User email: ${user.email}`);
      addDebug(`User role: ${user.role}`);
    }
  }, [user, authLoading]);

  const runTests = async () => {
    if (!user) {
      addDebug('❌ No user - cannot run tests');
      return;
    }

    setTesting(true);
    setTestResults({});
    addDebug('🧪 Starting Firestore tests...');

    try {
      // Test 1: Basic Firestore connection
      addDebug('📋 Test 1: Basic Firestore connection...');
      const groupsQuery = query(
        collection(db, 'groups'),
        where('privacy', '==', 'public'),
        limit(1)
      );
      
      const startTime = Date.now();
      const groupsSnap = await getDocs(groupsQuery);
      const endTime = Date.now();
      
      setTestResults(prev => ({
        ...prev,
        groupsQuery: {
          success: true,
          duration: endTime - startTime,
          count: groupsSnap.docs.length,
          error: null
        }
      }));
      
      addDebug(`✅ Groups query: ${groupsSnap.docs.length} docs in ${endTime - startTime}ms`);

      // Test 2: Public profiles
      addDebug('👥 Test 2: Public profiles query...');
      const profilesQuery = query(
        collection(db, 'publicProfiles'),
        where('isPublic', '==', true),
        limit(1)
      );
      
      const profilesStart = Date.now();
      const profilesSnap = await getDocs(profilesQuery);
      const profilesEnd = Date.now();
      
      setTestResults(prev => ({
        ...prev,
        profilesQuery: {
          success: true,
          duration: profilesEnd - profilesStart,
          count: profilesSnap.docs.length,
          error: null
        }
      }));
      
      addDebug(`✅ Profiles query: ${profilesSnap.docs.length} docs in ${profilesEnd - profilesStart}ms`);

      // Test 3: User memberships
      addDebug('🔗 Test 3: User memberships query...');
      const membershipsQuery = query(
        collection(db, 'groupMemberships'),
        where('userId', '==', user.uid),
        limit(1)
      );
      
      const membershipsStart = Date.now();
      const membershipsSnap = await getDocs(membershipsQuery);
      const membershipsEnd = Date.now();
      
      setTestResults(prev => ({
        ...prev,
        membershipsQuery: {
          success: true,
          duration: membershipsEnd - membershipsStart,
          count: membershipsSnap.docs.length,
          error: null
        }
      }));
      
      addDebug(`✅ Memberships query: ${membershipsSnap.docs.length} docs in ${membershipsEnd - membershipsStart}ms`);

      addDebug('🎉 All tests completed successfully!');

    } catch (error: any) {
      addDebug(`❌ Test failed: ${error.code} - ${error.message}`);
      setTestResults(prev => ({
        ...prev,
        error: {
          code: error.code,
          message: error.message,
          stack: error.stack
        }
      }));
    }

    setTesting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-gray-800">Loading Authentication...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Not Authenticated</h2>
          <p className="text-gray-600">Please sign in to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Community Debug</h1>
        
        {/* Auth Status */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Authentication Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <strong>User ID:</strong> {user.uid}
            </div>
            <div>
              <strong>Email:</strong> {user.email}
            </div>
            <div>
              <strong>Role:</strong> {user.role || 'user'}
            </div>
            <div>
              <strong>Display Name:</strong> {user.displayName || 'None'}
            </div>
          </div>
        </div>

        {/* Test Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Firestore Tests</h2>
          <button
            onClick={runTests}
            disabled={testing}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {testing ? 'Testing...' : 'Run Firestore Tests'}
          </button>
        </div>

        {/* Test Results */}
        {Object.keys(testResults).length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Test Results</h2>
            <pre className="bg-gray-100 p-4 rounded overflow-auto">
              {JSON.stringify(testResults, null, 2)}
            </pre>
          </div>
        )}

        {/* Debug Log */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Debug Log</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {debugInfo.map((info, index) => (
              <div key={index} className="text-sm font-mono bg-gray-50 p-2 rounded">
                {info}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
















